import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';

import StatusErrorDialog from 'components/Dialogs/StatusErrorDialog';
import StaffingClickableAlert from 'components/Staffing/Projects/StaffingClickableAlert';

/**
 * Builds the plain-text modal body (clipboard copy) for a failed Google Meet provision.
 *
 * Ops need both the human impact (no host, no waiting room) and the raw API string so they can
 * paste it into Slack or a ticket. Resync Calendar is the retry path after the Meet organizer
 * impersonation fix is deployed.
 *
 * @param {string} technicalError - `project.googleMeetCohostsError` from Firestore
 * @returns {string}
 */
export const buildGoogleMeetProvisioningFailedDetails = technicalError => {
  const details = String(technicalError || '').trim();

  return [
    'Control Room could not turn on Google Meet host management or add staff as co-hosts.',
    '',
    'What this means:',
    'People on this project will join as regular participants. Nobody is host, waiting room is off, and there are no meeting controls (mute all, admit from waiting room, etc.).',
    '',
    'What to do:',
    'Open the project and run Resync Calendar. That retries Meet host setup. This notice disappears when provisioning succeeds.',
    '',
    'Technical details:',
    details || '(no API error was stored on the project)',
  ].join('\n');
};

/**
 * Rich modal body: same copy as {@link buildGoogleMeetProvisioningFailedDetails}, with section
 * labels bold so ops can scan impact vs retry vs API error.
 *
 * @param {object} props
 * @param {string} props.technicalError - `project.googleMeetCohostsError`
 * @param {Record<string, string>} props.classes - `section` / `heading` from useStyles
 */
const GoogleMeetProvisioningFailedDetailsBody = ({ technicalError, classes }) => {
  const details = String(technicalError || '').trim();

  return (
    <>
      <p className={classes.section}>
        Control Room could not turn on Google Meet host management or add staff as co-hosts.
      </p>
      <p className={classes.section}>
        <strong className={classes.heading}>What this means:</strong>
        People on this project will join as regular participants. Nobody is host, waiting room is
        off, and there are no meeting controls (mute all, admit from waiting room, etc.).
      </p>
      <p className={classes.section}>
        <strong className={classes.heading}>What to do:</strong>
        Open the project and run Resync Calendar. That retries Meet host setup. This notice
        disappears when provisioning succeeds.
      </p>
      <p className={classes.section}>
        <strong className={classes.heading}>Technical details:</strong>
        {details || '(no API error was stored on the project)'}
      </p>
    </>
  );
};

GoogleMeetProvisioningFailedDetailsBody.propTypes = {
  technicalError: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

const useStyles = makeStyles({
  // First paragraph has no extra top margin; later sections space like the old blank lines.
  section: {
    margin: '0 0 1em',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  // Block so "What this means:" sits on its own line above the explanation.
  heading: {
    display: 'block',
    fontWeight: 700,
  },
});

/**
 * Clickable compact error Alert for a failed Google Meet provision. Opens
 * {@link StatusErrorDialog} with impact, retry instructions, and the stored API error.
 *
 * Local dialog state (not Redux InfoDialog) so project cards and project detail do not share one
 * global modal.
 *
 * @param {object} props
 * @param {string} props.errorMessage - `project.googleMeetCohostsError`
 * @param {string} [props.className] - extra class on the Alert (e.g. detail-page spacing)
 */
const GoogleMeetProvisioningFailedNotice = ({ errorMessage, className }) => {
  const classes = useStyles();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (!errorMessage) {
    return null;
  }

  return (
    <>
      <StaffingClickableAlert
        severity="error"
        className={className}
        onClick={() => setIsDialogOpen(true)}
      >
        Google Meet co-host provisioning failed
      </StaffingClickableAlert>
      <StatusErrorDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title="Google Meet co-host provisioning failed"
        statusError={buildGoogleMeetProvisioningFailedDetails(errorMessage)}
      >
        <GoogleMeetProvisioningFailedDetailsBody technicalError={errorMessage} classes={classes} />
      </StatusErrorDialog>
    </>
  );
};

GoogleMeetProvisioningFailedNotice.propTypes = {
  errorMessage: PropTypes.string,
  className: PropTypes.string,
};

export default GoogleMeetProvisioningFailedNotice;
